import { atomWithDebounce } from '@rematter/ui-web';
import type { Atom } from 'jotai';
import { atom } from 'jotai';

import { createGovReportingConfigMutationAtom } from '../_graphql/mutationGovReportingConfigCreate';
import { deleteGovReportingConfigMutationAtom } from '../_graphql/mutationGovReportingConfigDelete';
import { updateGovReportingConfigMutationAtom } from '../_graphql/mutationGovReportingConfigUpdate';
import { queryGovReportingConfigAtom } from '../_graphql/queryGovReportingConfig';
import type { GovReportingConfigRecord } from '../_graphql/queryGovReportingConfigs';
import { queryGovReportingConfigsAtom } from '../_graphql/queryGovReportingConfigs';

import { settingsRouteParamAtom } from './settingsModule.state';

import type { QueryAtomReadValue } from '~/shared/providers/api/createQueryAtomFactory';

export const $govReportingSearchTerm = atomWithDebounce('', 300);
export const $isGovReportingDialogOpen = atom(false);
export const $editingGovReportingConfig = atom<string | null>(null);

export const $govReportingConfigId = settingsRouteParamAtom(
  ['/compliance/reporting/:configId'],
  'configId',
);

export const $rawGovReportingConfigs = queryGovReportingConfigsAtom(() => ({}));

export const $govReportingConfigs: Atom<
  QueryAtomReadValue<GovReportingConfigRecord[]>
> = atom((get) => {
  const raw = get($rawGovReportingConfigs);
  const searchTerms = get($govReportingSearchTerm.debouncedValue)
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);

  const data = (raw.data ?? []).filter((config) => {
    if (searchTerms.length === 0) return true;
    const searchable = [config.name, config.serviceType]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return searchTerms.every((term) => searchable.includes(term));
  });

  return { ...raw, data };
});

export const $currentGovReportingConfig = queryGovReportingConfigAtom((get) => {
  const configId = get($govReportingConfigId);
  if (!configId) {
    return { variables: { id: '' }, skip: true };
  }
  return { variables: { id: configId } };
});

export const $createGovReportingConfig = createGovReportingConfigMutationAtom({
  onSuccess: (get) => {
    get($rawGovReportingConfigs).refetch?.();
  },
});

export const $updateGovReportingConfig = updateGovReportingConfigMutationAtom({
  onSuccess: (get) => {
    get($rawGovReportingConfigs).refetch?.();
    get($currentGovReportingConfig).refetch?.();
  },
});

export const $deleteGovReportingConfig = deleteGovReportingConfigMutationAtom({
  onSuccess: (get) => {
    get($rawGovReportingConfigs).refetch?.();
  },
});
